import ngeohash from "ngeohash"

/**
 * Get map bounds from a region
 * @param {Object} region - Region object with latitude, longitude, latitudeDelta, longitudeDelta
 * @returns {Object} Bounds object with north, south, east, west
 */
export const getMapBounds = region => {
  return {
    north: region.latitude + region.latitudeDelta / 2,
    south: region.latitude - region.latitudeDelta / 2,
    east: region.longitude + region.longitudeDelta / 2,
    west: region.longitude - region.longitudeDelta / 2,
  }
}

/**
 * Check if a point is within bounds
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @param {Object} bounds - Bounds object with north, south, east, west
 * @returns {boolean} True if point is within bounds
 */
export const isPointInBounds = (lat, lon, bounds) => {
  return lat >= bounds.south && lat <= bounds.north && lon >= bounds.west && lon <= bounds.east
}

/**
 * Generate geohash queries for a region
 * @param {number} latitude - Center latitude
 * @param {number} longitude - Center longitude
 * @param {number} radiusMiles - Search radius in miles
 * @returns {Object} Object with center geohash and neighbor geohashes
 */
export const generateGeohashQueries = (latitude, longitude, radiusMiles) => {
  // Determine geohash precision based on radius
  // Smaller radius = higher precision (more characters)
  let precision
  if (radiusMiles <= 1) {
    precision = 7 // ~153m x 153m
  } else if (radiusMiles <= 5) {
    precision = 6 // ~1.2km x 609m
  } else if (radiusMiles <= 20) {
    precision = 5 // ~4.9km x 4.9km
  } else if (radiusMiles <= 100) {
    precision = 4 // ~39km x 19.5km
  } else {
    precision = 3 // ~156km x 156km
  }

  // Generate center geohash
  const centerGeohash = ngeohash.encode(latitude, longitude, precision)

  // Get neighboring geohashes to cover the entire search area
  const neighbors = ngeohash.neighbors(centerGeohash)

  // Include center and all 8 neighbors
  const allGeohashes = [centerGeohash, ...Object.values(neighbors)]

  return {
    center: centerGeohash,
    neighbors: allGeohashes,
    precision,
  }
}

/**
 * Calculate distance between two points using Haversine formula
 * @param {number} lat1 - First point latitude
 * @param {number} lon1 - First point longitude
 * @param {number} lat2 - Second point latitude
 * @param {number} lon2 - Second point longitude
 * @returns {number} Distance in miles
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3959 // Earth's radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

